// src/components/StatusInstructions.js
// Instructions de statut — overview-first accordion. Every status renders as
// a row grouped by semantic family (the app's status-dot language) showing
// at a glance whether it's customized ("Personnalisé" chip + preview) or on
// the default. Expanding a row reveals the default definition (verbatim —
// it IS the prompt text sent to the AI) and the override textarea.
// Each status the AI may set also carries « L'IA ne pose jamais ce statut »:
// the account excludes it from the AI's choices; the team still sets it.
// API: GET/PUT /api/logistics/status-instructions
//   → {statuses: [{value, default_definition, custom_instruction,
//                  ai_settable, ai_disabled}], disabled_statuses: [value]}.
//   PUT {instructions, disabled_statuses, inactive_statuses, carrier_scopes}.
//   inactive_statuses: the statuses on which the AI does nothing (resolved ones always, ai_inactive_locked).
//   carrier_scopes (KI-315): status → the carriers the AI uses it for (none = every carrier);
//   carrier_options: the account's carriers (canonical names) a scope can name.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FaSpinner, FaCheckCircle, FaTimesCircle, FaChevronRight, FaChevronDown } from 'react-icons/fa';
import './StatusInstructions.css';
import { getStatusLabel, getStatusClass } from '../constants/ticketStatuses';

// Semantic family → group + dot color, mirroring the app-wide status chips.
const FAMILY_OF_CLASS = {
  'status-new': 'open',
  'status-open': 'open',
  'status-processing': 'processing',
  'status-waiting': 'waiting',
  'status-pending': 'waiting',
  'status-automated': 'processing',
  'status-resolved': 'terminal',
  'status-closed': 'terminal',
};

const FAMILY_DOT = {
  open: 'var(--blue)',
  waiting: 'var(--state-warning)',
  processing: 'var(--violet)',
  terminal: 'var(--state-success)',
  other: 'var(--text-muted)',
};

const FAMILY_ORDER = ['open', 'waiting', 'processing', 'terminal', 'other'];

// The carriers a status is restricted to, as sent to the API: every status the
// AI may set, an empty list meaning « every carrier » (it clears a scope).
export const carrierScopesPayload = (entries, scopes) => {
  const payload = {};
  (entries || []).filter((entry) => entry.ai_settable === true).forEach((entry) => {
    payload[entry.value] = [...((scopes || {})[entry.value] || [])];
  });
  return payload;
};

const StatusInstructions = () => {
  const { t } = useTranslation();
  const [statuses, setStatuses] = useState(null); // null = loading
  const [instructions, setInstructions] = useState({});
  const [aiDisabled, setAiDisabled] = useState({});
  const [aiInactive, setAiInactive] = useState({});
  const [carrierScopes, setCarrierScopes] = useState({});
  const [carrierOptions, setCarrierOptions] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const feedbackTimer = useRef(null);

  const showFeedback = useCallback((type, message) => {
    clearTimeout(feedbackTimer.current);
    setFeedback({ type, message });
    if (type === 'success') {
      feedbackTimer.current = setTimeout(() => setFeedback(null), 5000);
    }
  }, []);

  useEffect(() => () => clearTimeout(feedbackTimer.current), []);

  const applyEntries = (entries, options = []) => {
    setStatuses(entries);
    const current = {};
    const disabled = {};
    const inactive = {};
    const scopes = {};
    entries.forEach((entry) => {
      current[entry.value] = entry.custom_instruction || '';
      disabled[entry.value] = entry.ai_disabled === true;
      inactive[entry.value] = entry.ai_inactive === true;
      scopes[entry.value] = Array.isArray(entry.carrier_scope) ? entry.carrier_scope : [];
    });
    setInstructions(current);
    setAiDisabled(disabled);
    setAiInactive(inactive);
    setCarrierScopes(scopes);
    setCarrierOptions(Array.isArray(options) ? options : []);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
        const response = await axios.get('/api/logistics/status-instructions', { headers });
        applyEntries(response.data.statuses || [], response.data.carrier_options);
      } catch (error) {
        console.error('Failed to fetch status instructions:', error);
        setStatuses([]);
        showFeedback('error', t('statusInstructions.loadError', 'Impossible de charger les instructions de statut.'));
      }
    };
    fetchData();
  }, [t, showFeedback]);

  const handleChange = (status, value) => {
    setInstructions((prev) => ({ ...prev, [status]: value }));
    setIsDirty(true);
    setFeedback(null);
  };

  const handleAiDisabledChange = (status, checked) => {
    setAiDisabled((prev) => ({ ...prev, [status]: checked }));
    setIsDirty(true);
    setFeedback(null);
  };

  const handleAiInactiveChange = (status, checked) => {
    setAiInactive((prev) => ({ ...prev, [status]: checked }));
    setIsDirty(true);
    setFeedback(null);
  };

  // carrier === null: « Tous les transporteurs » (clears the scope).
  const handleCarrierScopeChange = (status, carrier, checked) => {
    setCarrierScopes((prev) => {
      const current = prev[status] || [];
      let next = [];
      if (carrier !== null) {
        next = checked ? [...current.filter((c) => c !== carrier), carrier] : current.filter((c) => c !== carrier);
      }
      return { ...prev, [status]: next };
    });
    setIsDirty(true);
    setFeedback(null);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setFeedback(null);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
      const disabledStatuses = (statuses || [])
        .filter((entry) => entry.ai_settable === true && aiDisabled[entry.value])
        .map((entry) => entry.value);
      const inactiveStatuses = (statuses || [])
        .filter((entry) => entry.ai_inactive_locked !== true && aiInactive[entry.value])
        .map((entry) => entry.value);
      const response = await axios.put(
        '/api/logistics/status-instructions',
        {
          instructions,
          disabled_statuses: disabledStatuses,
          inactive_statuses: inactiveStatuses,
          carrier_scopes: carrierScopesPayload(statuses, carrierScopes),
        },
        { headers },
      );
      applyEntries(response.data.statuses || [], response.data.carrier_options);
      setIsDirty(false);
      showFeedback('success', t('statusInstructions.saved', 'Instructions de statut enregistrées.'));
    } catch (error) {
      console.error('Failed to save status instructions:', error);
      showFeedback('error', t('statusInstructions.saveError', 'Échec de l’enregistrement des instructions.'));
    } finally {
      setIsSaving(false);
    }
  };

  const loading = statuses === null;
  const entries = statuses || [];

  const familyOf = (value) => FAMILY_OF_CLASS[getStatusClass(value)] || 'other';
  const groups = FAMILY_ORDER
    .map((family) => ({ family, entries: entries.filter((e) => familyOf(e.value) === family) }))
    .filter((g) => g.entries.length > 0);

  const FAMILY_LABELS = {
    open: t('statusInstructions.groupOpen', 'Ouverts'),
    waiting: t('statusInstructions.groupWaiting', 'En attente'),
    processing: t('statusInstructions.groupProcessing', 'En traitement'),
    terminal: t('statusInstructions.groupTerminal', 'Terminés'),
    other: t('statusInstructions.groupOther', 'Autres'),
  };

  const customizedCount = entries.filter((e) => (instructions[e.value] || '').trim()).length;
  const excludedCount = entries.filter((e) => e.ai_settable === true && aiDisabled[e.value]).length;

  const firstLine = (text) => {
    const line = (text || '').trim().split('\n')[0];
    return line.length > 110 ? `${line.slice(0, 110)}…` : line;
  };

  return (
    <div className="status-instructions-page">
      <div className="si-card">
        {/* Header */}
        <div className="si-card-header">
          <div className="si-heading">
            <h1>{t('statusInstructions.title', 'Instructions de statut')}</h1>
            <p>{t('statusInstructions.subtitle', 'Guidez l’IA qui définit le statut des tickets après chaque message. Un champ vide conserve le comportement par défaut.')}</p>
          </div>
          <div className="si-header-actions">
            {!loading && (
              <span className="si-count">
                {customizedCount > 0
                  ? t('statusInstructions.countCustom', '{{total}} statuts · {{custom}} personnalisés', { total: entries.length, custom: customizedCount })
                  : t('statusInstructions.countAll', '{{total}} statuts', { total: entries.length })}
              </span>
            )}
            {!loading && excludedCount > 0 && (
              <span className="si-count">
                {t('statusInstructions.countExcluded', 'Jamais posés par l’IA : {{n}}', { n: excludedCount })}
              </span>
            )}
            {isDirty && !isSaving && (
              <span className="si-dirty-hint">{t('statusInstructions.unsaved', 'Modifications non enregistrées')}</span>
            )}
            <button type="button" className="u-btn u-btn--primary" onClick={handleSave} disabled={isSaving || !isDirty}>
              {isSaving
                ? (<><FaSpinner className="si-spin" /> {t('statusInstructions.saving', 'Enregistrement…')}</>)
                : t('statusInstructions.save', 'Enregistrer')}
            </button>
          </div>
        </div>

        {feedback && (
          <div className={`si-feedback ${feedback.type}`} role="status">
            {feedback.type === 'success' ? <FaCheckCircle /> : <FaTimesCircle />}
            {feedback.message}
          </div>
        )}

        {loading ? (
          <div className="si-skeleton" aria-hidden="true">
            {Array.from({ length: 8 }, (_, i) => <div key={i} className="si-skeleton-row" />)}
          </div>
        ) : (
          groups.map((group) => (
            <div key={group.family} className="si-group">
              <h2 className="si-group-title">
                <span className="si-dot" style={{ background: FAMILY_DOT[group.family] }} aria-hidden="true" />
                {FAMILY_LABELS[group.family]}
                <span className="si-group-count">{group.entries.length}</span>
              </h2>
              <ul className="si-list">
                {group.entries.map((entry) => {
                  const isOpen = !!expanded[entry.value];
                  const override = instructions[entry.value] || '';
                  const hasOverride = override.trim().length > 0;
                  const isExcluded = entry.ai_settable === true && !!aiDisabled[entry.value];
                  const isInactive = entry.ai_inactive_locked === true || !!aiInactive[entry.value];
                  const scope = carrierScopes[entry.value] || [];
                  const hasScope = entry.ai_settable === true && !isExcluded && scope.length > 0;
                  return (
                    <li key={entry.value} className={`si-row ${isOpen ? 'open' : ''}`}>
                      <button
                        type="button"
                        className="si-row-head"
                        aria-expanded={isOpen}
                        onClick={() => setExpanded((prev) => ({ ...prev, [entry.value]: !prev[entry.value] }))}
                      >
                        <span className="si-row-caret" aria-hidden="true">
                          {isOpen ? <FaChevronDown /> : <FaChevronRight />}
                        </span>
                        <span className="si-row-label">{getStatusLabel(t, entry.value)}</span>
                        {isExcluded && (
                          <span className="si-chip-excluded">{t('statusInstructions.excludedChip', 'Jamais par l’IA')}</span>
                        )}
                        {isInactive && (
                          <span className="si-chip-excluded">{t('statusInstructions.inactiveChip', 'IA à l’arrêt')}</span>
                        )}
                        {hasScope && (
                          <span className="si-chip-scope">
                            {t('statusInstructions.carrierScopeChip', 'Seulement : {{carriers}}', { carriers: scope.join(', ') })}
                          </span>
                        )}
                        {hasOverride ? (
                          <>
                            <span className="si-chip-custom">{t('statusInstructions.customChip', 'Personnalisé')}</span>
                            {!isOpen && (
                              <span className="si-row-preview" title={override}>{firstLine(override)}</span>
                            )}
                          </>
                        ) : (
                          <span className="si-row-default">{t('statusInstructions.defaultChip', 'Par défaut')}</span>
                        )}
                      </button>
                      {isOpen && (
                        <div className="si-row-body">
                          {entry.ai_settable === true && (
                            <label className="si-ai-toggle" htmlFor={`si-ai-disabled-${entry.value}`}>
                              <input
                                id={`si-ai-disabled-${entry.value}`}
                                type="checkbox"
                                checked={isExcluded}
                                onChange={(event) => handleAiDisabledChange(entry.value, event.target.checked)}
                              />
                              <span className="si-ai-toggle-text">
                                <span className="si-ai-toggle-label">
                                  {t('statusInstructions.aiNeverSets', 'L’IA ne pose jamais ce statut')}
                                </span>
                                <span className="si-ai-toggle-hint">
                                  {t('statusInstructions.aiNeverSetsHint', 'L’IA ne le choisit plus sur ce compte ; votre équipe peut toujours le poser à la main.')}
                                </span>
                              </span>
                            </label>
                          )}
                          <label className="si-ai-toggle" htmlFor={`si-ai-inactive-${entry.value}`}>
                            <input
                              id={`si-ai-inactive-${entry.value}`}
                              type="checkbox"
                              checked={isInactive}
                              disabled={entry.ai_inactive_locked === true}
                              onChange={(event) => handleAiInactiveChange(entry.value, event.target.checked)}
                            />
                            <span className="si-ai-toggle-text">
                              <span className="si-ai-toggle-label">
                                {t('statusInstructions.aiInactive', 'L’IA ne fait plus rien sur ce statut')}
                              </span>
                              <span className="si-ai-toggle-hint">
                                {entry.ai_inactive_locked === true
                                  ? t('statusInstructions.aiInactiveLocked', 'Toujours le cas pour un ticket résolu.')
                                  : t('statusInstructions.aiInactiveHint', 'Ni réponse, ni relance du transporteur, ni réveil tant que le ticket a ce statut ; votre équipe garde la main.')}
                              </span>
                            </span>
                          </label>
                          {entry.ai_settable === true && !isExcluded && (
                            <fieldset className="si-carrier-scope">
                              <legend className="si-ai-toggle-label">
                                {t('statusInstructions.carrierScopeLabel', 'Transporteurs pour lesquels l’IA utilise ce statut')}
                              </legend>
                              <div className="si-carrier-options">
                                <label className={`si-carrier-option ${scope.length === 0 ? 'checked' : ''}`}>
                                  <input
                                    type="checkbox"
                                    checked={scope.length === 0}
                                    onChange={() => handleCarrierScopeChange(entry.value, null, true)}
                                  />
                                  {t('statusInstructions.carrierScopeAll', 'Tous les transporteurs')}
                                </label>
                                {carrierOptions.map((carrier) => (
                                  <label key={carrier} className={`si-carrier-option ${scope.includes(carrier) ? 'checked' : ''}`}>
                                    <input
                                      type="checkbox"
                                      checked={scope.includes(carrier)}
                                      onChange={(event) => handleCarrierScopeChange(entry.value, carrier, event.target.checked)}
                                    />
                                    {carrier}
                                  </label>
                                ))}
                              </div>
                              <span className="si-ai-toggle-hint">
                                {carrierOptions.length === 0
                                  ? t('statusInstructions.carrierScopeNone', 'Aucun transporteur configuré sur ce compte : ajoutez-les dans la page Transporteurs.')
                                  : t('statusInstructions.carrierScopeHint', 'Par défaut, tous. Si vous cochez des transporteurs, l’IA ne pose, ne garde ni ne propose ce statut que sur leurs tickets ; votre équipe peut toujours le poser. Chaque transporteur compte à part : « DPD » n’inclut pas « DPD B2B ».')}
                              </span>
                            </fieldset>
                          )}
                          {entry.ai_settable === false && (
                            <p className="si-ai-fixed">
                              {t('statusInstructions.aiNeverSetsFixed', 'Jamais posé par l’IA : ce statut vient de la création du ticket ou d’une assignation à un membre de l’équipe.')}
                            </p>
                          )}
                          <div className="si-default-block">
                            <span className="si-default-label">
                              {t('statusInstructions.defaultDefinition', 'Définition par défaut — envoyée à l’IA')}
                              <code>{entry.value}</code>
                            </span>
                            <p>{entry.default_definition}</p>
                          </div>
                          <div className="si-override">
                            <div className="si-override-head">
                              <label htmlFor={`si-override-${entry.value}`}>
                                {t('statusInstructions.overrideLabel', 'Instruction personnalisée')}
                              </label>
                              {hasOverride && (
                                <button
                                  type="button"
                                  className="si-clear-btn"
                                  onClick={() => handleChange(entry.value, '')}
                                >
                                  {t('statusInstructions.clearOverride', 'Effacer — revenir au défaut')}
                                </button>
                              )}
                            </div>
                            <textarea
                              id={`si-override-${entry.value}`}
                              rows={3}
                              placeholder={t('statusInstructions.overridePlaceholder', 'Optionnel : remplace la définition par défaut pour ce statut.')}
                              value={override}
                              onChange={(event) => handleChange(entry.value, event.target.value)}
                            />
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default StatusInstructions;
